import {
  RECORD_ENTITIES,
  RECORD_ENTITY_LABELS,
  noteTemplateScopeProblem,
  unknownNoteTemplateFields,
  type RecordEntity,
} from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Editor } from '@tiptap/react';
import { FileText, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { NoteBody } from '../components/notes/note-body.tsx';
import { MergeFieldPicker, useMergeFieldGroups } from '../components/notes/note-editor.tsx';
import { Gate } from '../components/shell/gate.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card } from '../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { EmptyState } from '../components/ui/empty-state.tsx';
import { Input } from '../components/ui/input.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import { RichTextEditor, isBlankHtml } from '../components/ui/rich-text.tsx';
import { describeError } from '../lib/api.ts';
import { api, type NoteTemplateView } from '../lib/client.ts';
import { useSession } from '../lib/session.tsx';

interface Draft {
  name: string;
  body: string;
  pinnedByDefault: boolean;
  global: boolean;
  entityTypes: RecordEntity[];
}

/** The record kinds in the order the dialog lists them: by name. */
const KINDS = [...RECORD_ENTITIES].sort((a, b) =>
  RECORD_ENTITY_LABELS[a].localeCompare(RECORD_ENTITY_LABELS[b]),
);

const EMPTY: Draft = { name: '', body: '', pinnedByDefault: false, global: false, entityTypes: [] };

const scopeLabel = (t: Pick<Draft, 'global' | 'entityTypes'>) =>
  t.global ? 'Every record' : t.entityTypes.map((k) => RECORD_ENTITY_LABELS[k]).join(', ');

/** Create or edit one template: name, where it is offered, pin by default, and the body. */
function TemplateDialog({
  editing,
  onClose,
}: {
  editing: { id: string | null; draft: Draft };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(editing.draft);
  const [editor, setEditor] = useState<Editor | null>(null);
  const onEditor = useCallback((e: Editor | null) => setEditor(e), []);
  const single = !draft.global && draft.entityTypes.length === 1 ? draft.entityTypes[0]! : null;
  const groups = useMergeFieldGroups(single);
  const session = useSession();
  // Every live definition, JSON ones included, as the API checks them (the picker offers fewer).
  const defs = useQuery({
    queryKey: ['field-definitions', single],
    queryFn: () => api.fieldDefinitions.list(single!),
    enabled: single !== null && session.isEnabled('custom_fields.enabled'),
  });
  const customKeys = single ? { [single]: (defs.data ?? []).map((d) => d.key) } : {};
  // As the API: on an edit that keeps the kinds, fields the stored body already names pass, so a
  // template whose custom field was retired can still be renamed or fixed.
  const sameScope =
    editing.id !== null &&
    editing.draft.global === draft.global &&
    editing.draft.entityTypes.join() === draft.entityTypes.join();
  const kept = sameScope ? unknownNoteTemplateFields(editing.draft.body, draft, customKeys) : [];
  const unknown =
    draft.body && !defs.isLoading
      ? unknownNoteTemplateFields(draft.body, draft, customKeys).filter((f) => !kept.includes(f))
      : [];
  const scopeProblem = noteTemplateScopeProblem(draft);
  const save = useMutation({
    mutationFn: () =>
      editing.id ? api.noteTemplates.patch(editing.id, draft) : api.noteTemplates.create(draft),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['note-templates'] });
      onClose();
    },
  });
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const toggleKind = (kind: RecordEntity) =>
    set({
      entityTypes: draft.entityTypes.includes(kind)
        ? draft.entityTypes.filter((k) => k !== kind)
        : KINDS.filter((k) => k === kind || draft.entityTypes.includes(k)),
    });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="note-template-dialog">
        <DialogHeader>
          <DialogTitle>{editing.id ? 'Edit note template' : 'New note template'}</DialogTitle>
          <DialogDescription>
            Inserted from the note editor. Notes already written from it never change.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            Name
            <Input
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              aria-label="Template name"
              maxLength={120}
            />
          </label>
          <fieldset className="flex flex-col gap-1.5 text-sm">
            <legend className="mb-1">Offered on</legend>
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={draft.global}
                onChange={(e) => set({ global: e.target.checked, entityTypes: [] })}
              />
              Every kind of record (global)
            </label>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {KINDS.map((kind) => (
                <label key={kind} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    disabled={draft.global}
                    checked={draft.entityTypes.includes(kind)}
                    onChange={() => toggleKind(kind)}
                  />
                  {RECORD_ENTITY_LABELS[kind]}
                </label>
              ))}
            </div>
            {!single && (
              <p className="text-muted-foreground text-xs">
                On several kinds of record, or everywhere, a template can use only the record's
                name, your name and today's date.
              </p>
            )}
          </fieldset>
          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={draft.pinnedByDefault}
              onChange={(e) => set({ pinnedByDefault: e.target.checked })}
            />
            Pin notes written from it
          </label>
          <RichTextEditor
            label="Template body"
            value={editing.draft.body}
            onChange={(body) => set({ body })}
            onEditor={onEditor}
            className="max-h-[50vh] min-h-40"
            toolbar={
              <MergeFieldPicker
                groups={groups}
                onPick={(path) => editor?.chain().focus().insertContent(`{{${path}}}`).run()}
              />
            }
          />
          {unknown.length > 0 && (
            <p className="text-danger text-xs" role="alert" data-testid="unknown-fields">
              These merge fields are not available here: {unknown.map((p) => `{{${p}}}`).join(', ')}
            </p>
          )}
          {save.error && (
            <p className="text-danger text-sm" role="alert">
              {describeError(save.error)}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !draft.name.trim() ||
                isBlankHtml(draft.body) ||
                scopeProblem !== null ||
                unknown.length > 0 ||
                save.isPending
              }
              data-testid="save-template"
            >
              {save.isPending && <Loader2 className="animate-spin" />} Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesList() {
  const qc = useQueryClient();
  const templates = useQuery({
    queryKey: ['note-templates', 'all'],
    queryFn: () => api.noteTemplates.list(),
  });
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.noteTemplates.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['note-templates'] }),
  });
  const edit = (t: NoteTemplateView) =>
    setEditing({
      id: t.id,
      draft: {
        name: t.name,
        body: t.body,
        pinnedByDefault: t.pinnedByDefault,
        global: t.global,
        entityTypes: [...t.entityTypes],
      },
    });
  const add = (
    <Button onClick={() => setEditing({ id: null, draft: EMPTY })} data-testid="new-template">
      <Plus /> New template
    </Button>
  );
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Note templates"
        description="Note skeletons the note editor offers under Insert template, for every record or some kinds of record."
        actions={add}
      />
      {templates.isPending && <p className="text-muted-foreground text-sm">Loading…</p>}
      {templates.error && (
        <p className="text-danger text-sm" role="alert">
          {describeError(templates.error)}
        </p>
      )}
      {remove.error && (
        <p className="text-danger text-sm" role="alert">
          {describeError(remove.error)}
        </p>
      )}
      {templates.data?.length === 0 && (
        <EmptyState
          icon={FileText}
          title="No note templates yet"
          description="A template is inserted where the cursor is, with the record's fields filled in."
        />
      )}
      <ul className="grid gap-3 lg:grid-cols-2">
        {templates.data?.map((t) => (
          <li key={t.id}>
            <Card className="flex flex-col gap-2 p-4" data-testid="note-template">
              <div className="flex items-start gap-2">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="font-medium">{t.name}</span>
                  <span className="flex flex-wrap gap-1">
                    <Badge variant="secondary">{scopeLabel(t)}</Badge>
                    {t.pinnedByDefault && <Badge variant="secondary">Pinned</Badge>}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Edit ${t.name}`}
                  onClick={() => edit(t)}
                >
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${t.name}`}
                  disabled={remove.isPending}
                  onClick={() => {
                    if (
                      window.confirm(`Delete the template "${t.name}"? Notes written from it stay.`)
                    )
                      remove.mutate(t.id);
                  }}
                >
                  <Trash2 />
                </Button>
              </div>
              <div className="bg-muted/40 max-h-40 overflow-hidden rounded-md p-2">
                <NoteBody body={t.body} format="html" />
              </div>
            </Card>
          </li>
        ))}
      </ul>
      {editing && <TemplateDialog editing={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

/** Settings → Note templates: `note_template.manage`, behind `notes.templates`. */
export function NoteTemplatesPage() {
  return (
    <Gate permission="note_template.manage" feature="notes.templates">
      <TemplatesList />
    </Gate>
  );
}
